// Main app layout with responsive sidebar, theme toggle, skip-to-content link, and dark mode.
// The desktop sidebar minimises to a 64px icon rail (not fully hidden) via a collapse
// control; the preference is persisted in localStorage so it survives reloads. Rail
// tooltips are portal-rendered via RailTooltip so the rail nav can scroll on short
// viewports without clipping them. Mobile is unaffected — it keeps the independent
// hamburger overlay.
// Nav groups are assembled by buildNavigationGroups() from navigationRegistry — the
// command palette consumes the same factory so gating logic stays in one place.
// PaletteProvider wraps the layout tree so the ⌘K keybinding + portal are available on
// all authed routes without mounting on the public /login page.

import { Outlet, Link, useLocation } from "react-router";
import {
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { useState, useMemo, useCallback, Suspense } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useProfile } from "../../hooks/useProfile";
import { clsx } from "clsx";
import Button from "./Button";
import ErrorBoundary from "./ErrorBoundary";
import LoadingSpinner from "./LoadingSpinner";
import logoHorizontal from "../../assets/contractgen-logo-horizontal.svg";
import logoHorizontalDark from "../../assets/contractgen-logo-horizontal-dark.svg";
import logomark from "../../assets/contractgen-logomark.svg";
import logomarkWhite from "../../assets/contractgen-logomark-white.svg";
import {
  buildNavigationGroups,
  type NavItem,
  type NavGroup,
} from "./navigationRegistry";
import { PaletteProvider } from "../palette/PaletteProvider";
import { PaletteTrigger } from "../palette/PaletteTrigger";
import { shortcutLabel } from "../palette/platform";
import { RailTooltip } from "./RailTooltip";

type Theme = "system" | "light" | "dark";
const themeOrder: Theme[] = ["system", "light", "dark"];
const themeLabels: Record<Theme, string> = {
  system: "System",
  light: "Light",
  dark: "Dark",
};
const themeIcons: Record<Theme, typeof Sun> = {
  system: Monitor,
  light: Sun,
  dark: Moon,
};

const COLLAPSE_STORAGE_KEY = "contractgen-sidebar-collapsed";

// 44×44 rail control base (colours applied per-state at the call site).
// No focus classes: the global :focus-visible rule (index.css) supplies
// the brand ring app-wide, same as the expanded nav links.
const railBtn =
  "flex h-11 w-11 items-center justify-center rounded-lg transition-colors";
const railIdle =
  "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700";
// One tint step stronger than the expanded nav's brand-50 — no text label to
// reinforce the active state in the rail.
const railActive =
  "bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300";

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop-only minimise preference, persisted in localStorage. Independent of the
  // mobile `sidebarOpen` overlay state above.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const setCollapsed = useCallback((collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    try {
      localStorage.setItem(COLLAPSE_STORAGE_KEY, String(collapsed));
    } catch {
      /* noop — preference is best-effort */
    }
  }, []);
  const { user, logout, hasArea } = useAuth();
  const { data: profile } = useProfile();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const displayName = profile?.name || user?.email;
  const logo = resolvedTheme === "dark" ? logoHorizontalDark : logoHorizontal;
  const logomarkSrc = resolvedTheme === "dark" ? logomarkWhite : logomark;
  const location = useLocation();
  const themeKey = theme as Theme;
  const themeLabel = themeLabels[themeKey] || "System";

  // Build navigation groups based on user role and area permissions.
  // Delegates to buildNavigationGroups() so sidebar and palette share one
  // gated source of truth.
  const navigationGroups = useMemo(
    () => buildNavigationGroups(user?.role, hasArea),
    [user?.role, hasArea],
  );

  const cycleTheme = () => {
    const currentIndex = themeOrder.indexOf(themeKey);
    const nextIndex = (currentIndex + 1) % themeOrder.length;
    setTheme(themeOrder[nextIndex]);
  };

  const ThemeIcon = themeIcons[themeKey] || Monitor;

  function renderNavGroup(group: NavGroup, index: number) {
    return (
      <div key={group.label || `group-${index}`}>
        {group.label && (
          <p className="px-4 pt-4 pb-1 text-overline uppercase text-slate-500 dark:text-slate-400">
            {group.label}
          </p>
        )}
        {group.items.map(renderNavLink)}
      </div>
    );
  }

  const isNavItemActive = (item: NavItem) =>
    item.href === "/"
      ? location.pathname === "/"
      : location.pathname === item.href ||
        location.pathname.startsWith(item.href + "/");

  function renderNavLink(item: NavItem) {
    const isActive = isNavItemActive(item);
    return (
      <Link
        key={item.name}
        to={item.href}
        aria-current={isActive ? "page" : undefined}
        className={clsx(
          "flex items-center px-4 py-3 text-body-sm font-medium rounded-lg mb-1",
          isActive
            ? "bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300"
            : "text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700",
        )}
      >
        <item.icon className="h-5 w-5 mr-3" />
        {item.name}
      </Link>
    );
  }

  function renderRailGroup(group: NavGroup, index: number) {
    return (
      <div
        key={group.label || `group-${index}`}
        className="flex flex-col items-center gap-0.5"
      >
        {index > 0 && (
          <div
            aria-hidden="true"
            className="my-2 h-px w-6 bg-slate-200 dark:bg-slate-700"
          />
        )}
        {group.items.map(renderRailLink)}
      </div>
    );
  }

  function renderRailLink(item: NavItem) {
    const isActive = isNavItemActive(item);
    return (
      <RailTooltip key={item.name} label={item.name}>
        <Link
          to={item.href}
          aria-label={item.name}
          aria-current={isActive ? "page" : undefined}
          className={clsx(railBtn, isActive ? railActive : railIdle)}
        >
          <item.icon className="h-5 w-5" />
        </Link>
      </RailTooltip>
    );
  }

  return (
    <PaletteProvider>
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Skip-to-main link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-4 focus:left-4 focus:px-4 focus:py-2 focus:bg-brand-600 focus:text-white focus:rounded-md"
      >
        Skip to main content
      </a>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-600/75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={clsx(
          "fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-slate-800 shadow-lg transform transition-transform lg:hidden",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200 dark:border-slate-700">
          <img src={logo} alt="ContractGen" className="h-10" />
          <Button
            variant="ghost"
            iconOnly
            size="lg"
            icon={<X className="h-6 w-6" />}
            aria-label="Close sidebar"
            onClick={() => setSidebarOpen(false)}
          />
        </div>
        {/* Full trigger at top of mobile drawer nav */}
        <div className="px-3 pt-3 pb-1">
          <PaletteTrigger variant="full" />
        </div>
        <nav className="mt-2 px-2 overflow-y-auto">
          {navigationGroups.map(renderNavGroup)}
        </nav>
      </div>

      {/* Desktop sidebar — collapses to a 64px icon rail. The container always
          renders (width animates 256↔64); content swaps at the state change.
          overflow-hidden only while expanded: the fixed w-64 inner stops labels
          wrapping mid-animation, while the rail keeps default overflow (its
          tooltips are portal-rendered, so clipping is moot anyway). */}
      <div
        id="desktop-sidebar"
        className={clsx(
          "hidden lg:fixed lg:inset-y-0 lg:z-30 lg:flex lg:flex-col",
          "bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700",
          "transition-[width] duration-200 ease-out motion-reduce:transition-none",
          sidebarCollapsed ? "lg:w-16" : "lg:w-64 overflow-hidden",
        )}
      >
        {sidebarCollapsed ? (
          <div className="flex min-h-0 w-16 flex-col grow items-center pb-3">
            {/* Head: standalone logomark, theme-swapped like the wordmark */}
            <div className="flex h-16 w-full flex-none items-center justify-center border-b border-slate-200 dark:border-slate-700">
              <img src={logomarkSrc} alt="ContractGen" className="h-[26px]" />
            </div>

            {/* Expand + search */}
            <div className="mt-2.5 flex flex-col items-center gap-0.5">
              <RailTooltip label="Expand sidebar">
                <button
                  type="button"
                  onClick={() => setCollapsed(false)}
                  aria-label="Expand sidebar"
                  aria-expanded={false}
                  aria-controls="desktop-sidebar"
                  className={clsx(railBtn, railIdle)}
                >
                  <PanelLeftOpen className="h-5 w-5" />
                </button>
              </RailTooltip>
              <RailTooltip label={`Search… ${shortcutLabel()}`}>
                <PaletteTrigger variant="iconOnly" />
              </RailTooltip>
            </div>

            {/* Nav — scrolls on short viewports; portal tooltips are never
                clipped. min-h-0 (here AND on the rail root) is load-bearing:
                flex items default to min-height:auto, which would let the nav
                push the foot off-screen instead of shrinking into a scroll
                region. */}
            <nav
              aria-label="Primary"
              className="mt-1 flex min-h-0 w-full flex-1 flex-col items-center overflow-y-auto"
            >
              {navigationGroups.map(renderRailGroup)}
            </nav>

            {/* Foot: avatar disc, theme cycle, sign out — outside the scroll
                area so they stay reachable on short viewports */}
            <div className="flex w-full flex-col items-center gap-1 border-t border-slate-200 dark:border-slate-700 pt-2.5">
              <RailTooltip label={`${displayName} · ${user?.role}`}>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30">
                  <span className="text-body-sm font-medium text-brand-700 dark:text-brand-300">
                    {(displayName || "?").charAt(0).toUpperCase()}
                  </span>
                </span>
              </RailTooltip>
              <RailTooltip label={`Theme: ${themeLabel}`}>
                <button
                  type="button"
                  onClick={cycleTheme}
                  aria-label={`Theme: ${themeLabel}`}
                  className={clsx(railBtn, railIdle)}
                >
                  <ThemeIcon className="h-5 w-5" />
                </button>
              </RailTooltip>
              <RailTooltip label="Sign out">
                <button
                  type="button"
                  onClick={logout}
                  aria-label="Sign out"
                  className={clsx(railBtn, railIdle)}
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </RailTooltip>
            </div>
          </div>
        ) : (
        <div className="flex w-64 flex-col grow">
          <div className="flex items-center justify-between h-16 pl-6 pr-2.5 border-b border-slate-200 dark:border-slate-700">
            <img src={logo} alt="ContractGen" className="h-10" />
            <Button
              variant="ghost"
              iconOnly
              size="sm"
              icon={<PanelLeftClose className="h-5 w-5" />}
              aria-label="Collapse sidebar"
              aria-expanded={true}
              aria-controls="desktop-sidebar"
              onClick={() => setCollapsed(true)}
            />
          </div>
          {/* Full search trigger between logo header and nav links */}
          <div className="px-3 pt-3 pb-1">
            <PaletteTrigger variant="full" />
          </div>
          <nav className="flex-1 mt-2 px-3">
            {navigationGroups.map(renderNavGroup)}
          </nav>
          <div className="p-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center">
                <span className="text-body-sm font-medium text-brand-700 dark:text-brand-300">
                  {(displayName || "?").charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="ml-3 overflow-hidden">
                <p className="text-body-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                  {displayName}
                </p>
                <p className="text-caption text-slate-500 dark:text-slate-400 capitalize">
                  {user?.role}
                </p>
              </div>
            </div>
            {/* Theme toggle */}
            <button
              type="button"
              onClick={cycleTheme}
              aria-label={`Theme: ${themeLabel}`}
              className="flex items-center w-full px-3 py-2 mb-2 text-body-sm font-medium text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <ThemeIcon className="h-5 w-5 mr-3" />
              {themeLabel}
            </button>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              icon={<LogOut className="h-5 w-5" />}
              onClick={logout}
              className="w-full justify-start"
            >
              Sign out
            </Button>
          </div>
        </div>
        )}
      </div>

      {/* Main content — padding tracks the sidebar width (rail or expanded) */}
      <div
        className={clsx(
          "transition-[padding] duration-200 motion-reduce:transition-none",
          sidebarCollapsed ? "lg:pl-16" : "lg:pl-64",
        )}
      >
        {/* Mobile header */}
        <div className="sticky top-0 z-30 flex items-center h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 lg:hidden">
          <Button
            variant="ghost"
            iconOnly
            size="lg"
            icon={<Menu className="h-6 w-6" />}
            aria-label="Open menu"
            onClick={() => setSidebarOpen(true)}
          />
          <img src={logo} alt="ContractGen" className="ml-4 h-8" />
          {/* Icon-only trigger in the mobile sticky header */}
          <div className="ml-auto">
            <PaletteTrigger variant="iconOnly" />
          </div>
        </div>

        <main id="main-content" className="px-4 sm:px-6 lg:px-8 py-8 bg-page-wash dark:bg-none">
          {/* Route-level boundary: resets on navigation so a one-off
              render crash on a single page does not wedge the whole
              app — the user can still use the sidebar to escape. */}
          <ErrorBoundary
            resetKey={location.pathname}
            scopeLabel="this page"
          >
            {/* Suspense fallback for lazy-loaded route chunks (admin section,
                drafting wizard). Shows a brief centred spinner on first
                navigation to a split route while its chunk is fetched. */}
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-24">
                  <LoadingSpinner size="lg" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
    </PaletteProvider>
  );
}
